import { getSummonerVfxSpec } from './summonerSpellVfxSpecs.js';

/**
 * createSummonerSpellVfx — Short-lived cast meshes per summoner spell (OCP).
 * Shapes: sphere / ring / flame / column / spark / trail / shield (AB-10).
 */
export function createSummonerSpellVfx({ THREE, scene } = {}) {
  const active = [];

  function play(spellId, ctx = {}) {
    const spec = getSummonerVfxSpec(spellId);
    if (!spec || !THREE || !scene) return false;
    const meshes = buildMeshes(THREE, spec, ctx);
    meshes.forEach((mesh) => {
      scene.add(mesh);
      active.push({ mesh, life: spec.duration, max: spec.duration });
    });
    return meshes.length > 0;
  }

  function tick(dt) {
    for (let i = active.length - 1; i >= 0; i -= 1) {
      const fx = active[i];
      fx.life -= dt;
      const t = Math.max(0, fx.life / fx.max);
      if (fx.mesh.material) fx.mesh.material.opacity = t;
      if (fx.mesh.scale && typeof fx.mesh.scale.setScalar === 'function') {
        fx.mesh.scale.setScalar(0.6 + (1 - t) * 0.8);
      }
      if (fx.life <= 0) {
        scene.remove(fx.mesh);
        active.splice(i, 1);
      }
    }
  }

  return { play, tick, getActiveCount: () => active.length };
}

function buildMeshes(THREE, spec, ctx) {
  if (spec.anchor === 'flash_path') return buildFlashPath(THREE, spec, ctx);
  const pos = resolveAnchor(spec.anchor, ctx);
  if (!pos) return [];
  if (spec.shape === 'spark') {
    return [
      makeBurst(THREE, { ...spec, shape: 'sphere', scale: spec.scale * 0.7 }, pos.x - 0.25, pos.z),
      makeBurst(THREE, { ...spec, shape: 'sphere', scale: spec.scale * 0.55 }, pos.x + 0.3, pos.z + 0.15)
    ];
  }
  if (spec.shape === 'trail') {
    return [
      makeBurst(THREE, { ...spec, shape: 'ring', scale: spec.scale }, pos.x, pos.z),
      makeBurst(THREE, { ...spec, shape: 'ring', scale: spec.scale * 0.7 }, pos.x, pos.z)
    ];
  }
  return [makeBurst(THREE, spec, pos.x, pos.z)];
}

function resolveAnchor(anchor, ctx) {
  if (anchor === 'target' && ctx.target) {
    return { x: ctx.target.x, z: ctx.target.z ?? ctx.target.y ?? 0 };
  }
  if (ctx.caster) {
    return { x: ctx.caster.x, z: ctx.caster.z ?? ctx.caster.y ?? 0 };
  }
  return null;
}

function buildFlashPath(THREE, spec, ctx) {
  const from = ctx.from || ctx.caster;
  const to = ctx.to || ctx.cursor;
  if (!from || !to) return [];
  const fx = ctx.from?.z ?? from.z ?? from.y ?? 0;
  const tz = to.z ?? to.y ?? 0;
  return [
    makeBurst(THREE, spec, from.x, fx),
    makeBurst(THREE, { ...spec, scale: spec.scale * 0.85 }, to.x, tz)
  ];
}

function makeBurst(THREE, spec, x, z) {
  const mat = new THREE.MeshBasicMaterial({
    color: spec.color,
    transparent: true,
    opacity: 0.9,
    side: THREE.DoubleSide
  });
  const s = 0.45 * (spec.scale || 1);
  const geo = geometryForShape(THREE, spec.shape, s);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(x, heightForShape(spec.shape), z);
  return mesh;
}

function geometryForShape(THREE, shape, s) {
  if (shape === 'ring' || shape === 'shield' || shape === 'trail') {
    return new THREE.RingGeometry(s * 0.55, s, 24).rotateX(-Math.PI / 2);
  }
  if (shape === 'flame') {
    return new THREE.ConeGeometry(s * 0.45, s * 1.6, 8);
  }
  if (shape === 'column') {
    return new THREE.CylinderGeometry(s * 0.25, s * 0.35, s * 1.8, 10);
  }
  return new THREE.SphereGeometry(s, 10, 10);
}

function heightForShape(shape) {
  if (shape === 'ring' || shape === 'shield' || shape === 'trail') return 0.15;
  if (shape === 'flame' || shape === 'column') return 1.0;
  return 1.1;
}
